import React, { useState } from 'react';
import { PersonFinal } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Mail, Phone, CheckCircle, AlertCircle, Loader2, UserCog } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export default function PersonaContactUploader() {
  const [emailFile, setEmailFile] = useState(null);
  const [phoneFile, setPhoneFile] = useState(null);
  const [archetypeFile, setArchetypeFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });
      rows.push(row);
    }
    
    return rows;
  };

  const handleEmailUpload = async () => {
    if (!emailFile) return;
    
    setIsProcessing(true);
    setProgress(0);
    setResults(null);
    
    try {
      const text = await emailFile.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        setResults({ success: false, message: 'No data found in CSV' });
        return;
      }
      
      // Validate headers
      const firstRow = rows[0];
      if (!('person_id' in firstRow) || !('email' in firstRow)) {
        setResults({ success: false, message: 'CSV must have person_id and email columns' });
        return;
      }
      
      // Fetch all personas
      const allPersonas = await PersonFinal.filter({});
      const personaMap = new Map();
      allPersonas.forEach(p => {
        if (p.person_id) personaMap.set(p.person_id, p);
      });
      
      let updated = 0;
      let notFound = 0;
      let errors = [];
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const personId = row.person_id;
        const email = row.email;
        
        if (!personId || !email) {
          errors.push(`Row ${i + 2}: Missing person_id or email`);
          continue;
        }
        
        const persona = personaMap.get(personId);
        if (!persona) {
          notFound++;
          continue;
        }
        
        try {
          await PersonFinal.update(persona.id, { email });
          updated++;
        } catch (err) {
          errors.push(`Row ${i + 2}: Failed to update ${personId}`);
        }
        
        setProgress(Math.round(((i + 1) / rows.length) * 100));
      }
      
      setResults({
        success: true,
        message: `Updated ${updated} personas. ${notFound} not found.`,
        errors: errors.slice(0, 10)
      });
      
    } catch (error) {
      setResults({ success: false, message: error.message });
    } finally {
      setIsProcessing(false);
      setProgress(100);
    }
  };

  const handlePhoneUpload = async () => {
    if (!phoneFile) return;
    
    setIsProcessing(true);
    setProgress(0);
    setResults(null);
    
    try {
      const text = await phoneFile.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        setResults({ success: false, message: 'No data found in CSV' });
        return;
      }
      
      // Validate headers
      const firstRow = rows[0];
      if (!('account_name' in firstRow) || !('phone' in firstRow)) {
        setResults({ success: false, message: 'CSV must have account_name and phone columns' });
        return;
      }
      
      // Build account_name -> phone map
      const phoneMap = new Map();
      rows.forEach(row => {
        if (row.account_name && row.phone) {
          phoneMap.set(row.account_name.toLowerCase().trim(), row.phone);
        }
      });
      
      // Fetch all personas
      const allPersonas = await PersonFinal.filter({});
      
      let updated = 0;
      let skipped = 0;
      let errors = [];
      
      const matchingPersonas = allPersonas.filter(p => {
        if (!p.account_name) return false;
        return phoneMap.has(p.account_name.toLowerCase().trim());
      });
      
      for (let i = 0; i < matchingPersonas.length; i++) {
        const persona = matchingPersonas[i];
        const phone = phoneMap.get(persona.account_name.toLowerCase().trim());
        
        try {
          await PersonFinal.update(persona.id, { phone });
          updated++;
        } catch (err) {
          errors.push(`Failed to update ${persona.first_name} ${persona.last_name}`);
        }
        
        setProgress(Math.round(((i + 1) / matchingPersonas.length) * 100));
      }
      
      skipped = allPersonas.length - matchingPersonas.length;
      
      setResults({
        success: true,
        message: `Updated ${updated} personas with phone numbers. ${skipped} personas didn't match any account_name.`,
        errors: errors.slice(0, 10)
      });
      
    } catch (error) {
      setResults({ success: false, message: error.message });
    } finally {
      setIsProcessing(false);
      setProgress(100);
    }
  };

  const handleArchetypeUpload = async () => {
    if (!archetypeFile) return;
    
    setIsProcessing(true);
    setProgress(0);
    setResults(null);
    
    try {
      const text = await archetypeFile.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        setResults({ success: false, message: 'No data found in CSV' });
        return;
      }
      
      // Validate headers
      const firstRow = rows[0];
      if (!('person_id' in firstRow) || !('archetype_name' in firstRow)) {
        setResults({ success: false, message: 'CSV must have person_id and archetype_name columns' });
        return;
      }
      
      // Fetch all personas
      const allPersonas = await PersonFinal.filter({});
      const personaMap = new Map();
      allPersonas.forEach(p => {
        if (p.person_id) personaMap.set(p.person_id, p);
      });
      
      let updated = 0;
      let notFound = 0;
      let errors = [];
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const personId = row.person_id;
        const archetypeName = row.archetype_name;
        
        if (!personId || !archetypeName) {
          errors.push(`Row ${i + 2}: Missing person_id or archetype_name`);
          continue;
        }
        
        const persona = personaMap.get(personId);
        if (!persona) {
          notFound++;
          continue;
        }
        
        try {
          await PersonFinal.update(persona.id, { archetype_name: archetypeName });
          updated++;
        } catch (err) {
          errors.push(`Row ${i + 2}: Failed to update ${personId}`);
        }
        
        setProgress(Math.round(((i + 1) / rows.length) * 100));
      }
      
      setResults({
        success: true,
        message: `Updated ${updated} personas. ${notFound} not found.`,
        errors: errors.slice(0, 10)
      });
      
    } catch (error) {
      setResults({ success: false, message: error.message });
    } finally {
      setIsProcessing(false);
      setProgress(100);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Persona Contact Uploader
        </CardTitle>
        <CardDescription>
          Upload CSV files to update persona emails or phone numbers
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="email">
          <TabsList className="mb-4">
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email by Person ID
            </TabsTrigger>
            <TabsTrigger value="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone by Account Name
            </TabsTrigger>
            <TabsTrigger value="archetype" className="flex items-center gap-2">
              <UserCog className="w-4 h-4" />
              Archetype by Person ID
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="email" className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-blue-800 mb-2">CSV Format Required:</p>
              <code className="bg-blue-100 px-2 py-1 rounded text-blue-900">person_id,email</code>
              <p className="text-blue-700 mt-2">Example:</p>
              <pre className="bg-blue-100 p-2 rounded mt-1 text-xs">
{`person_id,email
john_doe_123,john@example.com
jane_smith_456,jane@example.com`}
              </pre>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email-file">Select CSV File</Label>
              <Input
                id="email-file"
                type="file"
                accept=".csv"
                onChange={(e) => setEmailFile(e.target.files?.[0] || null)}
                disabled={isProcessing}
              />
            </div>
            
            <Button 
              onClick={handleEmailUpload} 
              disabled={!emailFile || isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Update Emails
                </>
              )}
            </Button>
          </TabsContent>
          
          <TabsContent value="phone" className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-green-800 mb-2">CSV Format Required:</p>
              <code className="bg-green-100 px-2 py-1 rounded text-green-900">account_name,phone</code>
              <p className="text-green-700 mt-2">Example:</p>
              <pre className="bg-green-100 p-2 rounded mt-1 text-xs">
{`account_name,phone
Acme Corporation,+1-555-123-4567
Tech Solutions Inc,+1-555-987-6543`}
              </pre>
              <p className="text-green-700 mt-2 text-xs">
                All personas matching each account_name will receive the phone number.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone-file">Select CSV File</Label>
              <Input
                id="phone-file"
                type="file"
                accept=".csv"
                onChange={(e) => setPhoneFile(e.target.files?.[0] || null)}
                disabled={isProcessing}
              />
            </div>
            
            <Button 
              onClick={handlePhoneUpload} 
              disabled={!phoneFile || isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Update Phones
                </>
              )}
            </Button>
          </TabsContent>
          
          <TabsContent value="archetype" className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm">
              <p className="font-medium text-purple-800 mb-2">CSV Format Required:</p>
              <code className="bg-purple-100 px-2 py-1 rounded text-purple-900">person_id,archetype_name</code>
              <p className="text-purple-700 mt-2">Example:</p>
              <pre className="bg-purple-100 p-2 rounded mt-1 text-xs">
{`person_id,archetype_name
john_doe_123,CIO
jane_smith_456,IT Director`}
              </pre>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="archetype-file">Select CSV File</Label>
              <Input
                id="archetype-file"
                type="file"
                accept=".csv"
                onChange={(e) => setArchetypeFile(e.target.files?.[0] || null)}
                disabled={isProcessing}
              />
            </div>
            
            <Button 
              onClick={handleArchetypeUpload} 
              disabled={!archetypeFile || isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Update Archetypes
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>
        
        {isProcessing && (
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-gray-500 mt-1 text-center">{progress}% complete</p>
          </div>
        )}
        
        {results && (
          <Alert className={`mt-4 ${results.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            {results.success ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription className={results.success ? 'text-green-800' : 'text-red-800'}>
              {results.message}
              {results.errors && results.errors.length > 0 && (
                <ul className="mt-2 text-xs list-disc list-inside">
                  {results.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}